import type { ComboboxStore } from '@mantine/core';
import { type Dispatch, type SetStateAction, useEffect, useMemo, useRef } from 'react';
import { isGroup } from './group';
import type { ComboboxItemWithFormatted, SelectCommonProps, SelectDataProps } from './props';

type Props<TData extends ComboboxItemWithFormatted<TValue>, TValue extends string> = Pick<
  SelectCommonProps,
  'maxItemVisible' | 'onSearchChange'
> &
  Pick<SelectDataProps<TData>, 'data'> & {
    combobox: ComboboxStore;
    searchValue: string;
    setSearchValue: Dispatch<SetStateAction<string>>;
  };

export const useSelect = <TData extends ComboboxItemWithFormatted<TValue>, TValue extends string>({
  combobox,
  data,
  maxItemVisible,
  searchValue,
  setSearchValue,
  onSearchChange,
}: Props<TData, TValue>) => {
  const onSearchChangeTimer = useRef<number | null>(null);

  const filteredData = useMemo(() => {
    const lowerSearch = searchValue.trim().toLowerCase();
    const matchesSearch = (label: string | undefined) => !label || label.trim().toLowerCase().includes(lowerSearch);

    // Support both flat items and ComboboxItemGroup entries
    const processed = (data as unknown[]).flatMap<TData>(item => {
      if (isGroup(item)) {
        // Filter group sub-items by search, return group if any match
        const matchedItems = item.items.filter(sub =>
          typeof sub === 'string' ? matchesSearch(sub) : matchesSearch(sub.label),
        );
        return matchedItems.length > 0 ? [{ ...item, items: matchedItems } as unknown as TData] : [];
      }
      return matchesSearch((item as TData).label) ? [item as TData] : [];
    });

    // Only sort flat items; preserve group order
    const hasGroups = (data as unknown[]).some(isGroup);
    if (hasGroups) return processed;

    return processed.sort((a, b) => {
      if (a.disabled && !b.disabled) return 1;
      else if (!a.disabled && b.disabled) return -1;

      const aLabel = a.label?.trim().toLowerCase() ?? '';
      const bLabel = b.label?.trim().toLowerCase() ?? '';

      if (aLabel.startsWith(lowerSearch) && !bLabel.startsWith(lowerSearch)) return -1;
      else if (!aLabel.startsWith(lowerSearch) && bLabel.startsWith(lowerSearch)) return 1;
      return 0;
    });
  }, [data, searchValue]);
  const optionsMaxHeight = useMemo(() => {
    const _maxItemVisible = maxItemVisible && maxItemVisible > 0 ? maxItemVisible : 5;
    return _maxItemVisible * 32 + 8;
  }, [maxItemVisible]);

  useEffect(() => {
    // Delayed invoking event
    if (onSearchChangeTimer.current) {
      window.clearTimeout(onSearchChangeTimer.current);
    }
    onSearchChangeTimer.current = window.setTimeout(() => {
      onSearchChange?.(searchValue);
    }, 300);

    return () => {
      if (onSearchChangeTimer.current) {
        window.clearTimeout(onSearchChangeTimer.current);
      }
    };

    // excludes `onSearchChange` to avoid infinite loop
  }, [searchValue, onSearchChange]);

  return { combobox, filteredData, optionsMaxHeight, searchValue, setSearchValue };
};
